import { useState } from "react";
import { useTranslation } from "react-i18next";
import { StepTitle, AdStatusField, AgreeToArchiveField, CategoryField, ToggleStatusField, NameField } from "./fields";
import { MdExpandMore } from "react-icons/md";
import { Button } from "../../basic/Button";
import { Flex } from "../../../../../styled-system/jsx";
import AdPlanModal from "./AdPlanModal";
import { GOALS, AD_TYPES } from "./constants";
import WalmartSBFields from "./WalmartSBFields";
import { Row, Col } from "react-bootstrap";
import AssetSummary from "../AssetSelector/AssetSummary";
import AdPlanModerationWarnings from "./AdPlanModerationWarnings";
import CreativePreview from "../AssetSelector/CreativePreview";
import { Component as ReactCrop } from "react-image-crop";
import { useFetch } from "../../../../hooks/api";
import { getAdTypeName } from "../../../../utils/getAdTypeName";
import FormGroupTile from "../../basic/FormGroupTile";
import { css } from "../../../../../styled-system/css";
import { keepPreviousData } from "@tanstack/react-query";
import { useSimpleCan } from "../../../../config/casl/casl";
import { SDStoreLandingPageSelector } from "./fields/SDStoreLandingPageSelector";

// Map GOAL enum -> English label key in advertising.json (sentence-key form).
// Mirrors GOALS[*].label from ./constants but lets us run lookups through t().
const GOAL_LABEL_KEY_BY_VALUE = {
  [GOALS.DEMAND_GENERATION.value]: "Demand Generation",
  [GOALS.PRODUCT_LAUNCH.value]: "Product Launch",
  [GOALS.OPTIMIZE_ACOS.value]: "Optimize ACoS Profitability",
  [GOALS.SOV.value]: "Share of Voice",
  [GOALS.CUSTOM.value]: "Segmented Strategy",
  [GOALS.IMPORTED_ADS.value]: "Imported Ad",
};

// Map AD_TYPE enum -> English label key. Several AD_TYPES entries share a
// numeric value (e.g. SPONSORED_BRAND_V2 and SPONSORED_PRODUCT_GENERIC both
// use 19; CUSTOM and SPONSORED_PRODUCT_WALMART both use 20). getAdTypeName
// resolves these via Array.find on AD_TYPES, so the first-declared entry wins.
// We build the map in the same declaration order and only set keys that have
// no entry yet to match that semantics.
const AD_TYPE_KEY_PAIRS = [
  [AD_TYPES.SPONSORED_PRODUCT.value, "Sponsored Product"],
  [AD_TYPES.SPONSORED_BRAND.value, "Sponsored Brand"],
  [AD_TYPES.SPONSORED_BRAND_VIDEO.value, "Sponsored Brand: Video"],
  [AD_TYPES.SPONSORED_PRODUCT_AUTO.value, "Sponsored Product: Auto"],
  [AD_TYPES.SPONSORED_DISPLAY.value, "Sponsored Display (Legacy)"],
  [AD_TYPES.CUSTOM.value, "Custom"],
  [AD_TYPES.SPONSORED_DISPLAY_V2.value, "Sponsored Display"],
  [AD_TYPES.SPONSORED_BRAND_V2.value, "Sponsored Brand"],
  [AD_TYPES.SPONSORED_PRODUCT_GENERIC.value, "Sponsored Product: Generic"],
  [AD_TYPES.SPONSORED_PRODUCT_WALMART.value, "Sponsored Product"],
  [AD_TYPES.SPONSORED_PRODUCT_AUTO_WALMART.value, "Sponsored Product: Auto"],
  [AD_TYPES.SPONSORED_BRAND_WALMART.value, "Sponsored Brand"],
  [AD_TYPES.SPONSORED_BRAND_VIDEO_WALMART.value, "Sponsored Brand: Video"],
  [AD_TYPES.NON_TRELLIS_CAMPAIGN.value, "Non Trellis Managed Campaign"],
];
const AD_TYPE_LABEL_KEY_BY_VALUE = AD_TYPE_KEY_PAIRS.reduce((acc, [value, labelKey]) => {
  if (!(value in acc)) acc[value] = labelKey;
  return acc;
}, {});

const summaryContainer = css({
  padding: "0 xlarge",
});

const stepTitleStyle = css({
  color: "#484848",
  cursor: "pointer",
});

const reviewField = css({
  fontSize: "1.5rem",
  padding: "small",
});

const reviewLabel = css({
  width: "110px",
  color: "#73706E",
  fontWeight: 700,
});

const reviewValue = css({
  paddingLeft: "small",
  fontWeight: 300,
  color: "#73706E",
});

const stateContainer = css({
  margin: "medium small",
});

const creativeCheck = (adPlan) => {
  return (
    // Check for present of any option field for SD, otherwise always display creative for any type of SB media plan
    adPlan.creativeAsset ||
    adPlan.brandLogoAssetID ||
    adPlan.logo ||
    adPlan.headline ||
    [AD_TYPES.SPONSORED_BRAND.value, AD_TYPES.SPONSORED_BRAND_VIDEO.value].includes(adPlan.adType)
  );
};

const AdPlanSummary = ({
  setEditStep,
  setEditSubstep,
  setStep,
  adTypeFields,
  sbFields,
  targetingFields,
  state,
  onChange,
  onParentUpdate,
  marketPlace,
  errors,
  fieldsToValidate,
  adId,
  user,
  channel,
  categories,
  agreeToArchive,
  setAgreeToArchive,
  apiStatus,
  acosGoalsField,
  productFields,
  creativeProductFields,
  adPlanChildrenSummaryField,
  videoField,
  creativesAssetSelectionFields,
  storeSpotlightFields,
  review,
  isModal,
  isAdArchived = false,
}) => {
  const { t } = useTranslation(["advertising"]);
  const [showSetupFields, setShowSetupFields] = useState(true);
  const [showBrandSetupFields, setShowBrandSetupFields] = useState(true);
  const [showCreativeProductFields, setShowCreativeProductFields] = useState(true);
  const [showProductFields, setShowProductFields] = useState(true);
  const [showTargetingFields, setShowTargetingFields] = useState(true);
  const [inlineEditOpen, setInlineEditOpen] = useState(null);

  const userCanUpdateAds = useSimpleCan("update", "Ad") && !isAdArchived;

  const nonTrellisCampaignFlag = state.goal === GOALS.IMPORTED_ADS.value;

  const { data: categoryData } = useFetch(
    ["categories", state.categoryId],
    `/api/v2/categories/${state.categoryId}`,
    {},
    {
      select: (d) => d.data,
      placeholderData: keepPreviousData,
      enabled: !!state.categoryId,
    },
  );

  const getGoalName = (value) => {
    const labelKey = GOAL_LABEL_KEY_BY_VALUE[value];
    if (!labelKey) {
      return "";
    }
    return t(($) => $.advertising.goalLabel[labelKey]);
  };

  const getTranslatedAdTypeName = (value, currentChannel) => {
    let labelKey = AD_TYPE_LABEL_KEY_BY_VALUE[value];
    // Match getAdTypeName's channel-aware override: CUSTOM on Walmart renders
    // as Sponsored Product.
    if (value === AD_TYPES.CUSTOM.value && currentChannel === "walmart") {
      labelKey = "Sponsored Product";
    }
    if (!labelKey) {
      // Fallback to legacy label-from-constants path (e.g. ad types we haven't
      // mapped yet) so we never silently render nothing.
      const fallbackSelector = getAdTypeName(value, currentChannel);
      return fallbackSelector ? t(fallbackSelector) : "";
    }
    return t(($) => $.advertising.adTypeLabel[labelKey]);
  };

  return (
    <div className={summaryContainer}>
      <div className="d-flex justify-content-between">
        <h2 style={{ fontWeight: 700, marginTop: 0, fontSize: "24px" }}>
          {t(($) => $.advertising["Review: {name}"], { name: state.name })}
        </h2>
        {state.id && !state.canHaveChildren && userCanUpdateAds && (
          <ToggleStatusField onChange={onChange} state={state} />
        )}
      </div>
      <StepTitle
        children={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <span className={stepTitleStyle}>1. {t(($) => $.advertising.summaryStep.Setup)} </span>
              {userCanUpdateAds &&
                (adId || (!state.id && state.hasParent) ?
                  null
                : <Button
                    variant="icon"
                    onPress={() => {
                      setEditStep(0);
                      setEditSubstep(null);
                      setStep(0);
                    }}
                  >
                    <i className="fa fa-pencil fa-sm" style={{ fontSize: "16px" }} />
                  </Button>)}
            </div>
            <div>
              <MdExpandMore
                style={{
                  transform: showSetupFields ? "rotate(180deg)" : "rotate(0deg)",
                  fontSize: 32,
                  marginBottom: "-1rem",
                  cursor: "pointer",
                }}
                onClick={() => setShowSetupFields(!showSetupFields)}
              />
            </div>
          </div>
        }
      />
      {showSetupFields && (
        <div>
          {channel === "walmart" || state.goal === GOALS.IMPORTED_ADS.value ?
            <Flex className={`${reviewField} ${css({ alignItems: "center" })}`}>
              <span className={reviewLabel}>{t(($) => $.advertising.summaryLabel["Title:"])} </span>
              <span className={reviewValue}>{state.name}</span>
            </Flex>
          : <Flex className={`${reviewField} ${css({ alignItems: "center" })}`}>
              <span className={reviewLabel}>{t(($) => $.advertising.summaryLabel["Title:"])} </span>
              <div className={css({ width: "500px" })}>
                <NameField
                  state={state}
                  onChange={onChange}
                  errors={errors}
                  fieldsToValidate={fieldsToValidate}
                  isDisabled={!userCanUpdateAds}
                />
              </div>
            </Flex>
          }
          {!state.id && (
            <Flex className={reviewField}>
              <span className={reviewLabel}>{t(($) => $.advertising.summaryLabel["Category:"])} </span>
              <span className={reviewValue}>
                {categories?.find((c) => c.value === state.categoryId)?.label ?? categoryData?.name}
              </span>
            </Flex>
          )}
          <Flex className={reviewField}>
            <span className={reviewLabel}>{t(($) => $.advertising.summaryLabel["Strategy:"])} </span>
            <span className={reviewValue}>{getGoalName(state.goal)}</span>
          </Flex>
          <div>
            <Flex className={reviewField}>
              <span className={reviewLabel}>{t(($) => $.advertising.summaryLabel["Ad Type:"])} </span>
              <span className={reviewValue}>{getTranslatedAdTypeName(state.adType, channel)}</span>
            </Flex>
          </div>
          {state.id && (
            <Flex className={`${reviewField} d-flex align-items-center`}>
              <span className={reviewLabel}>{t(($) => $.advertising.summaryLabel["Category:"])} </span>
              <CategoryField
                categories={categories}
                state={state}
                dashboard={true}
                onChange={onChange}
                errors={errors}
                fieldsToValidate={fieldsToValidate}
                review={review}
                channel={channel}
                marketPlace={marketPlace}
                existingAdCategorySelector={true}
                isDisabled={!userCanUpdateAds}
              />
            </Flex>
          )}
          <Flex className={reviewField}>
            <div className={css({ width: "100%" })}>{acosGoalsField}</div>
          </Flex>

          <div className={`${css({ width: "100%" })} ${stateContainer}`}>
            <AdStatusField
              state={state}
              onChange={onChange}
              user={user}
              channel={channel}
              isDisabled={!userCanUpdateAds}
            />
          </div>
          {userCanUpdateAds && (
            <div className={stateContainer}>
              <AgreeToArchiveField
                state={state}
                agreeToArchive={agreeToArchive}
                setAgreeToArchive={setAgreeToArchive}
                user={user}
                apiStatus={apiStatus}
              />
            </div>
          )}
        </div>
      )}

      {/* COMPOSITE SUMMARY */}
      {state.canHaveChildren && (
        <>
          <StepTitle
            children={
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div>
                  <span className={stepTitleStyle}>2. {t(($) => $.advertising.summaryStep.Tactics)} </span>
                </div>
              </div>
            }
          />
          <div>{adPlanChildrenSummaryField}</div>
        </>
      )}
      {[AD_TYPES.SPONSORED_BRAND.value, AD_TYPES.SPONSORED_BRAND_VIDEO.value].includes(state.adType) && (
        <>
          <StepTitle
            children={
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginTop: "2rem",
                }}
              >
                <div style={{ display: "flex", alignItems: "center" }}>
                  <span className={stepTitleStyle}>2. {t(($) => $.advertising.summaryStep["Brand Setup"])} </span>
                  {userCanUpdateAds &&
                    (adId ? null : (
                      <Button
                        variant="icon"
                        onPress={() => {
                          setEditStep(0);
                          setEditSubstep(null);
                          setStep(0);
                        }}
                      >
                        <i className="fa fa-pencil fa-sm" style={{ fontSize: "16px" }} />
                      </Button>
                    ))}
                </div>
                <div>
                  <MdExpandMore
                    style={{
                      transform: showBrandSetupFields ? "rotate(180deg)" : "rotate(0deg)",
                      fontSize: 32,
                      marginBottom: "-1rem",
                      cursor: "pointer",
                    }}
                    onClick={() => setShowBrandSetupFields(!showBrandSetupFields)}
                  />
                </div>
              </div>
            }
          />

          {showBrandSetupFields && (
            <>
              {adId && state.policyViolations?.length > 0 && channel !== "walmart" && (
                <AdPlanModerationWarnings violations={state.policyViolations} />
              )}
              {sbFields}
              {state.adType === AD_TYPES.SPONSORED_BRAND_VIDEO.value && state.page?.value && (
                <div style={{ padding: "2rem 0" }}>{videoField}</div>
              )}
            </>
          )}
        </>
      )}
      {state.autoCollection && (
        <div style={{ marginTop: "1rem" }}>
          <small className="text-muted">
            {t(($) => $.advertising["Products, headline, and landing page are generated automatically by Amazon."])}
          </small>
          {state.excludedProducts?.length > 0 && (
            <div style={{ paddingTop: "1rem" }}>
              <Flex className={reviewField}>{productFields}</Flex>
            </div>
          )}
        </div>
      )}
      {(!state.storeLandingPage ||
        state.storeSpotlight ||
        !state.page?.label ||
        ((state.newProductCollection || state.manualCollection) && !state.page?.label)) &&
        !state.autoCollection &&
        !nonTrellisCampaignFlag && (
          <>
            <StepTitle
              overrideStyles={review ? { marginBottom: 0 } : {}}
              children={
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <span
                      className={`${stepTitleStyle} ${
                        state.adType === AD_TYPES.SPONSORED_BRAND_VIDEO.value && !state.logo && !state.id ? "mt-4" : ""
                      }`}
                    >
                      <span>
                        {(
                          state.canHaveChildren ||
                          [AD_TYPES.SPONSORED_BRAND.value, AD_TYPES.SPONSORED_BRAND_VIDEO.value].includes(state.adType)
                        ) ?
                          "3"
                        : "2"}
                      </span>
                      .{" "}
                      <span>
                        {state.storeSpotlight ?
                          t(($) => $.advertising.summaryStep["Store Spotlight Subpages"])
                        : (
                          state.newLandingPage ||
                          state.newProductCollection ||
                          state.manualCollection ||
                          (state.adType === AD_TYPES.SPONSORED_BRAND_VIDEO.value &&
                            !state.page?.value &&
                            state.newLandingPage)
                        ) ?
                          t(($) => $.advertising.summaryStep["Landing Page Products"])
                        : state.storeLandingPage || state.adType === AD_TYPES.SPONSORED_BRAND_VIDEO.value ?
                          t(($) => $.advertising.summaryStep["Creative Products"])
                        : t(($) => $.advertising.summaryStep["Ad Products"])}
                      </span>
                    </span>

                    {userCanUpdateAds &&
                      ((
                        (!state.id && state.hasParent) ||
                        state.adType === AD_TYPES.SPONSORED_BRAND.value ||
                        state.adType === AD_TYPES.SPONSORED_BRAND_VIDEO.value
                      ) ?
                        null
                      : adId ?
                        <>
                          <Button
                            variant="icon"
                            onPress={() => {
                              setEditStep(3);
                              setEditSubstep(0);
                              setStep(3);
                              setInlineEditOpen("products");
                            }}
                          >
                            <i className="fa fa-pencil fa-sm" style={{ fontSize: "16px" }} />
                          </Button>
                          <AdPlanModal
                            adId={state.id}
                            categoryId={state.categoryId}
                            categories={categories}
                            dashboard={false}
                            onParentUpdate={onParentUpdate}
                            initialAdPlan={state}
                            sectionEdit={true}
                            editStep={2}
                            isOpen={inlineEditOpen === "products"}
                            onClose={() => setInlineEditOpen(null)}
                          />
                        </>
                      : <Button
                          variant="icon"
                          onPress={() => {
                            setEditStep(2);
                            setEditSubstep(null);
                            setStep(2);
                          }}
                        >
                          <i className="fa fa-pencil fa-sm" style={{ fontSize: "16px" }} />
                        </Button>)}
                  </div>
                  <div>
                    <MdExpandMore
                      style={{
                        transform: showProductFields ? "rotate(180deg)" : "rotate(0deg)",
                        fontSize: 32,
                        marginBottom: "-1rem",
                        cursor: "pointer",
                      }}
                      onClick={() => setShowProductFields(!showProductFields)}
                    />
                  </div>
                </div>
              }
            />
            {showProductFields && (
              <>
                <div style={{ paddingTop: "1rem" }}>
                  <Flex className={reviewField}>{state.storeSpotlight ? storeSpotlightFields : productFields}</Flex>
                </div>
                {adTypeFields[0].slice(1)}
              </>
            )}
          </>
        )}
      {((state.adType === AD_TYPES.SPONSORED_BRAND.value &&
        (!state.storeSpotlight || state.newLandingPage) &&
        !state.autoCollection) ||
        (state.adType === AD_TYPES.SPONSORED_BRAND_VIDEO.value && state.logo)) && (
        <>
          <StepTitle
            overrideStyles={review ? { marginBottom: 0 } : {}}
            children={
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginTop: "2rem",
                }}
              >
                <div style={{ display: "flex", alignItems: "center" }}>
                  <span className={stepTitleStyle}>
                    <span>
                      {state.adType === (AD_TYPES.SPONSORED_BRAND_VIDEO.value && !state.logo) || state.page?.value ?
                        "3"
                      : "4"}
                    </span>
                    .{" "}
                    <span>
                      {state.storeSpotlight && state.newLandingPage ?
                        t(($) => $.advertising.summaryStep["Landing Page Products"])
                      : t(($) => $.advertising.summaryStep["Creative Products"])}
                    </span>
                  </span>

                  {userCanUpdateAds &&
                    ((!state.id && state.hasParent) || state.adType === AD_TYPES.SPONSORED_BRAND.value ? null
                    : adId ?
                      <>
                        <Button
                          variant="icon"
                          onPress={() => {
                            setEditStep(3);
                            setEditSubstep(0);
                            setStep(3);
                            setInlineEditOpen("creativeProducts");
                          }}
                        >
                          <i
                            className="fa fa-pencil fa-sm"
                            style={{
                              fontSize: "16px",
                            }}
                          />
                        </Button>
                        <AdPlanModal
                          adId={state.id}
                          categoryId={state.categoryId}
                          categories={categories}
                          dashboard={false}
                          onParentUpdate={onParentUpdate}
                          initialAdPlan={state}
                          sectionEdit={true}
                          editStep={2}
                          isOpen={inlineEditOpen === "creativeProducts"}
                          onClose={() => setInlineEditOpen(null)}
                        />
                      </>
                    : <Button
                        variant="icon"
                        onPress={() => {
                          setEditStep(2);
                          setEditSubstep(null);
                          setStep(2);
                        }}
                      >
                        <i className="fa fa-pencil fa-sm" style={{ fontSize: "16px" }} />
                      </Button>)}
                </div>
                <div>
                  <MdExpandMore
                    style={{
                      transform: showCreativeProductFields ? "rotate(180deg)" : "rotate(0deg)",
                      fontSize: 32,
                      marginBottom: "-1rem",
                      cursor: "pointer",
                    }}
                    onClick={() => setShowCreativeProductFields(!showCreativeProductFields)}
                  />
                </div>
              </div>
            }
          />
          {showCreativeProductFields && (
            <div style={{ paddingTop: "1rem" }}>
              <Flex className={reviewField}>
                {state.storeSpotlight && state.newLandingPage ? productFields : creativeProductFields}
              </Flex>
            </div>
          )}
        </>
      )}
      {([AD_TYPES.SPONSORED_BRAND_WALMART.value, AD_TYPES.SPONSORED_BRAND_VIDEO_WALMART.value].includes(state.adType) ||
        (state.adType === AD_TYPES.SPONSORED_DISPLAY_V2.value && creativeCheck(state))) && (
        <>
          <StepTitle
            children={
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginTop: "3rem",
                }}
              >
                <div style={{ display: "flex", alignItems: "center" }}>
                  <span className={stepTitleStyle}>3. {t(($) => $.advertising.summaryStep.Creatives)} </span>
                  {userCanUpdateAds &&
                    (adId && state.adType !== AD_TYPES.SPONSORED_DISPLAY_V2.value ? null
                    : adId ?
                      <>
                        <Button
                          variant="icon"
                          onPress={() => {
                            setEditStep(3);
                            setEditSubstep(null);
                            setStep(3);
                            setInlineEditOpen("creatives");
                          }}
                        >
                          <i
                            className="fa fa-pencil fa-sm"
                            style={{
                              fontSize: "16px",
                            }}
                          />
                        </Button>
                        <AdPlanModal
                          adId={state.id}
                          categoryId={state.categoryId}
                          categories={categories}
                          dashboard={false}
                          initialAdPlan={state}
                          sectionEdit={true}
                          editStep={3}
                          isOpen={inlineEditOpen === "creatives"}
                          onClose={() => setInlineEditOpen(null)}
                        />
                      </>
                    : <Button
                        variant="icon"
                        onPress={() => {
                          setEditStep(3);
                          setEditSubstep(null);
                          setStep(3);
                        }}
                      >
                        <i
                          className="fa fa-pencil fa-sm"
                          style={{
                            fontSize: "16px",
                          }}
                        />
                      </Button>)}
                </div>
                <div>
                  <MdExpandMore
                    style={{
                      transform: showBrandSetupFields ? "rotate(180deg)" : "rotate(0deg)",
                      fontSize: 32,
                      marginBottom: "-1rem",
                      cursor: "pointer",
                    }}
                    onClick={() => setShowBrandSetupFields(!showBrandSetupFields)}
                  />
                </div>
              </div>
            }
          />
          {showBrandSetupFields &&
            [AD_TYPES.SPONSORED_BRAND_WALMART.value, AD_TYPES.SPONSORED_BRAND_VIDEO_WALMART.value].includes(
              state.adType,
            ) && (
              <Row style={{ marginBottom: "1rem" }}>
                <Col xs={12}>
                  <WalmartSBFields
                    state={state}
                    errors={errors}
                    onChange={onChange}
                    adId={adId}
                    review={review}
                    isModal={isModal}
                    fieldsToValidate={fieldsToValidate}
                  />
                </Col>
              </Row>
            )}
          {creativeCheck(state) && showBrandSetupFields && (
            <>
              {adId && state.policyViolations?.length > 0 && channel !== "walmart" && (
                <AdPlanModerationWarnings violations={state.policyViolations} />
              )}
              {creativesAssetSelectionFields}
              <Row>
                <>
                  {typeof state.creativeModerationStatus === "string" &&
                    !["APPROVED", "PENDING_REVIEW", "FAILURE"]?.includes(state.creativeModerationStatus) && (
                      <small className="text-danger">{state.creativeModerationStatus}</small>
                    )}
                </>
                <AssetSummary
                  assetId={state.videoDetails?.asset_id ?? state.lifestyleImageDetails?.asset_id ?? state.video}
                  assetDescriptor={
                    state.lifestyleImageDetails?.type === "VIDEO" || state.videoDetails?.type === "VIDEO" ?
                      t(($) => $.advertising["Video Creative"])
                    : t(($) => $.advertising["Lifestyle Image"])
                  }
                  assetType={state.videoDetails?.type ?? state.lifestyleImageDetails?.type}
                  hidden={(!state.videoDetails && !state.lifestyleImageDetails) || state.sdRectangleCroppedArea?.height}
                  summaryContainerClasses={` ${state.videoDetails?.type === "VIDEO" ? "pe-5 my-0" : "pe-5 my-5"}`}
                />
                {state.sdRectangleCroppedArea?.height && (
                  <Col xs={12} lg={6} className={`${"pe-5 pt-5"} mt-1 col-lg-6 col-12"`}>
                    <h6
                      className="title mb-4 fs-4"
                      style={{
                        color: "#73706E",
                        fontWeight: "700",
                        textTransform: "none",
                      }}
                    >
                      {t(($) => $.advertising["Lifestyle Image (Cropped)"])}
                    </h6>
                    {state.lifestyleImageDetails?.name && (
                      <div className="mb-4">
                        <FormGroupTile
                          type="formcontrol"
                          defaultValue={state.lifestyleImageDetails?.name}
                          disabled={true}
                        />
                      </div>
                    )}
                    <ReactCrop crop={state.sdRectangleCroppedArea} zoom={1}>
                      <img
                        src={state.lifestyleImageDetails?.url_default}
                        alt={t(($) => $.advertising["Preview of cropped asset."])}
                        crossOrigin="anonymous"
                      />
                    </ReactCrop>
                  </Col>
                )}
                {state.sdSquareCroppedArea?.height && (
                  <Col xs={12} lg={6} className={`ps-5 pt-5 mt-1 col-lg-6 col-12"`}>
                    <h6
                      className="title mb-4 fs-4"
                      style={{
                        color: "#73706E",
                        fontWeight: "700",
                        textTransform: "none",
                      }}
                    >
                      {t(($) => $.advertising["Lifestyle Image (Square)"])}
                    </h6>
                    {state.lifestyleImageDetails?.name && (
                      <div className="mb-4">
                        <FormGroupTile
                          type="formcontrol"
                          defaultValue={state.lifestyleImageDetails?.name}
                          disabled={true}
                        />
                      </div>
                    )}
                    <ReactCrop crop={state.sdSquareCroppedArea} zoom={1} aspect={1}>
                      <img
                        src={state.lifestyleImageDetails?.url_default}
                        alt={t(($) => $.advertising["Preview of cropped asset."])}
                        crossOrigin="anonymous"
                        style={{ maxHeight: "300px", objectFit: "contain" }}
                      />
                    </ReactCrop>
                  </Col>
                )}

                <AssetSummary
                  assetId={state.logoDetails?.asset_id ?? state.logo}
                  assetDescriptor={t(($) => $.advertising["Brand Logo"])}
                  assetType={state.logoDetails?.type}
                  hidden={!state.logoDetails && !state.logo}
                  summaryContainerClasses={
                    (
                      (!state.videoDetails && !state.lifestyleImageDetails) ||
                      (state.lifestyleImageDetails && state.adType === AD_TYPES.SPONSORED_DISPLAY_V2.value)
                    ) ?
                      "pe-5 my-5"
                    : "ps-5 my-5"
                  }
                />
              </Row>
              {state.adType === AD_TYPES.SPONSORED_DISPLAY_V2.value && !isModal && (
                <Row className="mb-4">
                  <CreativePreview
                    state={state}
                    // Creative preview endpoint doesn't return HTML consistently with more than 1 ASIN, for now just include first selected
                    productId={state.product?.[0]}
                    marketPlace={marketPlace?.marketPlace}
                    showCreativePreview={true}
                    accordionLayout={false}
                  />
                </Row>
              )}
              {state.adType === AD_TYPES.SPONSORED_DISPLAY_V2.value && state.customSdStorePageEnabled && (
                <div className="mt-4">
                  <SDStoreLandingPageSelector
                    brandEntityId={state.brand?.value}
                    categoryId={state.categoryId ?? undefined}
                    store={state.store ?? null}
                    page={state.page ?? null}
                    hasRequiredCreatives={
                      !!(state.logoDetails && (state.lifestyleImageDetails || state.videoDetails) && state.headline)
                    }
                    customSdStorePageEnabled={!!state.customSdStorePageEnabled}
                    onChange={onChange}
                    disableFields={true}
                    errors={{}}
                  />
                </div>
              )}
            </>
          )}
        </>
      )}
      <StepTitle
        children={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <span className={stepTitleStyle}>
                <span>
                  {nonTrellisCampaignFlag ?
                    "2"
                  : (
                    state.canHaveChildren ||
                    [
                      AD_TYPES.SPONSORED_BRAND.value,
                      AD_TYPES.SPONSORED_BRAND_VIDEO.value,
                      AD_TYPES.SPONSORED_BRAND_WALMART.value,
                      AD_TYPES.SPONSORED_BRAND_VIDEO_WALMART.value,
                    ].includes(state.adType) ||
                    (state.adType === AD_TYPES.SPONSORED_DISPLAY_V2.value && creativeCheck(state))
                  ) ?
                    (
                      !state.page?.label &&
                      (state.adType !== AD_TYPES.SPONSORED_BRAND_VIDEO.value ||
                        (state.adType === AD_TYPES.SPONSORED_BRAND_VIDEO.value && state.logo))
                    ) ?
                      (
                        [
                          AD_TYPES.SPONSORED_DISPLAY_V2.value,
                          AD_TYPES.SPONSORED_BRAND_VIDEO_WALMART.value,
                          AD_TYPES.SPONSORED_BRAND_WALMART.value,
                          AD_TYPES.CUSTOM.value,
                        ]?.includes(state.adType) ||
                        (state.storeSpotlight && !state.newLandingPage)
                      ) ?
                        "4"
                      : "5"
                    : "4"
                  : "3"}
                </span>
                . {t(($) => $.advertising.summaryStep.Targeting)}{" "}
              </span>

              {userCanUpdateAds &&
                (!state.id && state.hasParent ? null
                : adId ?
                  <>
                    <Button
                      variant="icon"
                      onPress={() => {
                        const partialEditStep =
                          state.adType === AD_TYPES.SPONSORED_DISPLAY_V2.value ? 4
                          : state.storeSpotlight && !state.newLandingPage ? 2
                          : 3;
                        setEditStep(partialEditStep);
                        setEditSubstep(0);
                        setStep(partialEditStep);
                        setInlineEditOpen("targeting");
                      }}
                    >
                      <i
                        className="fa fa-pencil fa-sm"
                        style={{
                          fontSize: "16px",
                        }}
                      />
                    </Button>
                    <AdPlanModal
                      adId={state.id}
                      categoryId={state.categoryId}
                      categories={categories}
                      dashboard={false}
                      onParentUpdate={onParentUpdate}
                      initialAdPlan={state}
                      sectionEdit={true}
                      editStep={
                        state.adType === AD_TYPES.SPONSORED_DISPLAY_V2.value ? 4
                        : state.storeSpotlight && !state.newLandingPage ?
                          2
                        : 3
                      }
                      isOpen={inlineEditOpen === "targeting"}
                      onClose={() => setInlineEditOpen(null)}
                    />
                  </>
                : <Button
                    variant="icon"
                    onPress={() => {
                      const partialEditStep = state.adType === AD_TYPES.SPONSORED_DISPLAY_V2.value ? 4 : 3;
                      setEditStep(partialEditStep);
                      setEditSubstep(0);
                      setStep(partialEditStep);
                    }}
                  >
                    <i className="fa fa-pencil fa-sm" style={{ fontSize: "16px" }} />
                  </Button>)}
            </div>
            <div>
              <MdExpandMore
                style={{
                  transform: showTargetingFields ? "rotate(180deg)" : "rotate(0deg)",
                  fontSize: 32,
                  marginBottom: "-1rem",
                  cursor: "pointer",
                }}
                onClick={() => setShowTargetingFields(!showTargetingFields)}
              />
            </div>
          </div>
        }
      />
      {showTargetingFields && [...targetingFields]}
    </div>
  );
};

export default AdPlanSummary;
